/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { getUserId } from "Models/User";
import { Button, Card, Input, Progress, Stack } from "components";
import useCompanion from "hooks/useCompanion";
import useTryCatch from "hooks/useTryCatch";
import { updatePreferences } from "lib/preferencesApi";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import useEventRouting from "hooks/useEventRouting";
import ROUTES from "routes";
import { useUser } from "state/UserContext";
import { PageLayout } from "templates";
import usePreferences, { PREFERENCES_OPTIONS } from "./usePreferences";
import {
  appendOtherOptions,
  safeAppend,
} from "pages/Dashboard/GuestDetailsPage/GuestPreferencesPage";

import { useEffect, useMemo, useRef } from "react";
import { usePrefNavigator } from "state/PrefNavigatorContext";
import useRouting from "hooks/useRouting";
import usePreviousRoute from "utils/usePreviousRoute";
import { useTranslation } from "react-i18next";
import { pushToDataLayer } from "utils/gtm";
const Preferences = () => {
  const { t } = useTranslation();
  const { userId } = useParams();
  const [companion, setCompanion] = useCompanion(userId);
  const { setLoading, isLoading, userData } = useUser();
  const { navigateWithEvent, eventId } = useEventRouting();

  const {
    isPrefOther,
    setPrefOther,
    formData,
    customPref,
    setCustomPref,
    handleOnChange,
  } = usePreferences(companion);
  const { shouldEnterDonePage } = useRouting();
  const goBack = usePreviousRoute();

  const {
    progressBarValues,
    getNextStepName,
    getPreviousStepName,
    setCurrentStep,
  } = usePrefNavigator();
  useEffect(() => {
    setCurrentStep("preferences");
  }, []);
  const onContinue = () => {
    const nextStepName = getNextStepName();
    if (nextStepName && ROUTES.choices[nextStepName]) {
      const routePath = ROUTES.choices[nextStepName](userId);
      navigateWithEvent(routePath);
      setCurrentStep(nextStepName);
    } else {
      return null;
    }
  };

  const onGoBack = () => {
    pushToDataLayer({
      event: "preferences_back",
      screen_name: "dietary preferences",
      journey_step: "dietary",
    });
    const previousStepName = getPreviousStepName();
    if (previousStepName == null) {
      navigateWithEvent(ROUTES.choices.base(userId));
    }
    if (previousStepName) {
      goBack();
      setCurrentStep(previousStepName);
    }
  };

  const onSkip = async () => {
    pushToDataLayer({
      event: "skip_dietary_preferences",
      screen_name: "dietary preferences",
      journey_step: "dietary",
    });
    const nextStepName = getNextStepName();
    if (nextStepName == null) {
      navigateWithEvent(ROUTES.onboard.done);
    } else {
      onContinue();
    }
  };

  const handleOnSubmit = useTryCatch(async (e) => {
    e.preventDefault();
    pushToDataLayer({
      event: "next_dietary_preferences",
      screen_name: "dietary preferences",
      journey_step: "dietary",
    });
    // Input doesn't forward `required` to the DOM, so validate manually
    if (isPrefOther && !customPref.trim()) {
      return toast.error(t("common.messages.requiredField"));
    }
    let result = "";

    Object.values(formData).forEach(({ state, value }, i) => {
      if (state === "success" && value !== "") {
        if (value !== 0) {
          result = safeAppend(result, value, ",");
        }
      }
      return result;
    });
    if (customPref !== "") {
      result = appendOtherOptions(result, customPref);
    }
    if (result.length === 0) {
      return toast.error("Please choose an option.");
    }

    setLoading(true);
    const id = getUserId(companion) || userId;
    await updatePreferences(
      id,
      {
        isCompanion: id !== getUserId(userData),
        food_preferences: result,
      },
      eventId,
    );
    setLoading(false);

    const newCompanions = setCompanion({
      ...companion,
      food_preferences: result,
    });
    const nextStepName = getNextStepName();
    if (nextStepName === null) {
      const goTo = await shouldEnterDonePage(newCompanions);
      navigateWithEvent(goTo);
    } else {
      onContinue();
    }
  }, setLoading);

  const formRef = useRef(null);
  const isInitial = useMemo(() => {
    return (
      PREFERENCES_OPTIONS.every((key) => {
        if (key === "other") {
          return true;
        }
        return formData[key]?.state === "initial";
      }) && !isPrefOther
    );
  }, [formData, isPrefOther]);

  return (
    <PageLayout
      title={t("preferences.title")}
      note={t("preferences.description")}
      progressBarValues={progressBarValues}
      hasProgressBar
      hasGoBack
      goBackMethod={onGoBack}
      hasPolicy={false}
    >
      <Stack component="article" className="mt-10 max-w-[400px] mx-auto">
        <form
          ref={formRef}
          onSubmit={handleOnSubmit}
          className="justify-between sm:justify-center min-h-full"
        >
          <Stack className="space-y-4">
            {PREFERENCES_OPTIONS.map((key) => (
              <Card
                title={t(`preferences.${key}`)}
                key={key}
                variant="pref"
                state={
                  key === "other"
                    ? isPrefOther
                      ? "success"
                      : "initial"
                    : formData[key]?.state
                }
                onClick={
                  key === "other"
                    ? () => setPrefOther((prev) => !prev)
                    : () => handleOnChange(key)
                }
              />
            ))}
            <div>
              {isPrefOther && (
                <Input
                  placeholder={t("guestInfoPage.otherPlaceholder")}
                  name="other"
                  type="text"
                  label={t("preferences.otherLabel")}
                  required
                  value={customPref}
                  onChange={({ target: { value } }) => setCustomPref(value)}
                  spellCheck={false}
                  disabled={!isPrefOther}
                />
              )}
            </div>
          </Stack>
          <Stack className="mt-8">
            <div className="flex sm:hidden gap-x-0.5">
              {progressBarValues.map((value, index) => (
                <Progress key={index} value={value} />
              ))}
            </div>

            <div className="mt-2 w-full">
              <Button
                variant="primary"
                type="submit"
                isLoading={isLoading}
                fullWidth
                onClick={() => {
                  isInitial ? onSkip() : formRef.current?.requestSubmit();
                }}
              >
                {isInitial
                  ? t("preferences.skipButtonLabel")
                  : t("preferences.continueButtonLabel")}
              </Button>
            </div>
          </Stack>
        </form>
      </Stack>
    </PageLayout>
  );
};
export default Preferences;
